import { useState, useEffect, useRef } from 'react'
import { Modal } from './Modal'
import { Input } from './Input'
import { NumberInput } from './NumberInput'
import { Button } from './Button'
import { Search, Check } from 'lucide-react'

interface ProductItem {
  id: string
  codigo: string
  nome: string
  quantidade?: number
}

interface ProductSelectModalProps {
  isOpen: boolean
  onClose: () => void
  items: { id: string; codigo: string; nome: string }[]
  selectedItems: ProductItem[]
  onConfirm: (items: ProductItem[]) => void
  title: string
  searchPlaceholder?: string
  itemsPerPage?: number
}

export function ProductSelectModal({
  isOpen,
  onClose,
  items,
  selectedItems,
  onConfirm,
  title,
  searchPlaceholder = 'Pesquisar...',
  itemsPerPage = 50,
}: ProductSelectModalProps) {
  const [search, setSearch] = useState('')
  const [displayCount, setDisplayCount] = useState(itemsPerPage)
  const [internalSelected, setInternalSelected] = useState<ProductItem[]>(selectedItems)
  const observerTarget = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => { 
    setInternalSelected(selectedItems)
  }, [selectedItems])

  useEffect(() => {
    if (!isOpen) {
      setSearch('')
      setDisplayCount(itemsPerPage)
    }
  }, [isOpen, itemsPerPage])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setDisplayCount((prev) => prev + itemsPerPage)
        }
      },
      { 
        threshold: 0.1, 
        root: scrollContainerRef.current,
        rootMargin: '50px'
      }
    )

    const currentTarget = observerTarget.current
    if (currentTarget) {
      observer.observe(currentTarget)
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget)
      }
      observer.disconnect()
    }
  }, [itemsPerPage, isOpen])

  const toggleSelection = (item: { id: string; codigo: string; nome: string }) => {
    setInternalSelected((prev) => {
      const exists = prev.find((i) => i.id === item.id)
      if (exists) {
        return prev.filter((i) => i.id !== item.id)
      }
      return [...prev, { ...item, quantidade: 1 }]
    })
  }

  const updateQuantity = (id: string, quantidade: number) => {
    if (quantidade <= 0) {
      setInternalSelected((prev) => prev.filter((i) => i.id !== id))
      return
    }
    setInternalSelected((prev) =>
      prev.map((item) => (item.id === id ? { ...item, quantidade } : item))
    )
  }

  const handleConfirm = () => {
    onConfirm(internalSelected)
    onClose()
  }

  const handleCancel = () => {
    setInternalSelected(selectedItems)
    onClose()
  }

  // Filtrar e ordenar: selecionados no topo
  const filteredItems = items.filter(
    (item) =>
      (item.codigo?.toLowerCase() || '').includes(search.toLowerCase()) ||
      (item.nome?.toLowerCase() || '').includes(search.toLowerCase())
  )

  const selectedIds = new Set(internalSelected.map((i) => i.id))
  const sortedItems = [
    ...filteredItems.filter((item) => selectedIds.has(item.id)),
    ...filteredItems.filter((item) => !selectedIds.has(item.id)),
  ]

  const displayedItems = sortedItems.slice(0, displayCount)

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title={title} size="xl">
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setDisplayCount(itemsPerPage)
            }}
            className="pl-10"
          />
        </div>

        {/* Selected Count */}
        {internalSelected.length > 0 && (
          <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 px-3 py-2 text-sm font-medium text-purple-700 dark:text-purple-300">
            {internalSelected.length} {internalSelected.length === 1 ? 'produto selecionado' : 'produtos selecionados'}
          </div>
        )}

        {/* Items List */}
        <div ref={scrollContainerRef} className="max-h-[50vh] sm:max-h-[600px] space-y-3 overflow-y-auto">
          {displayedItems.map((item) => {
            const selectedItem = internalSelected.find((i) => i.id === item.id)
            const isSelected = !!selectedItem

            return (
              <div
                key={item.id}
                className={`rounded-lg border-2 p-3 sm:p-4 transition-colors ${
                  isSelected
                    ? 'border-purple-500 bg-purple-50 dark:border-purple-600 dark:bg-purple-900/30'
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <button
                    onClick={() => toggleSelection(item)}
                    className={`flex min-h-[44px] min-w-[44px] sm:h-6 sm:w-6 items-center justify-center rounded border-2 transition-colors flex-shrink-0 ${
                      isSelected
                        ? 'border-purple-500 bg-purple-500 dark:border-purple-600 dark:bg-purple-600'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {isSelected && <Check className="h-5 w-5 sm:h-4 sm:w-4 text-white" />}
                  </button>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="font-semibold text-sm text-purple-700 dark:text-purple-400">
                      {item.codigo}
                    </div>
                    <p className="text-base font-medium text-gray-900 dark:text-white whitespace-normal break-words leading-relaxed">
                      {item.nome}
                    </p>
                  </div>
                </div>

                {/* Quantity Input - Full width on mobile */}
                {isSelected && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <NumberInput
                      value={selectedItem?.quantidade || 1}
                      onChange={(value) => updateQuantity(item.id, value)}
                      min={1}
                      showControls={true}
                    />
                  </div>
                )}
              </div>
            )
          })}

          {/* Intersection Observer Target */}
          {displayedItems.length < sortedItems.length && (
            <div ref={observerTarget} className="py-4 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">Carregando mais...</p>
            </div>
          )}

          {filteredItems.length === 0 && (
            <div className="py-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">Nenhum produto encontrado</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <Button variant="outline" onClick={handleCancel} className="w-full sm:w-auto order-2 sm:order-1">
            Cancelar
          </Button>
          <Button onClick={handleConfirm} size="lg" className="w-full sm:w-auto order-1 sm:order-2">
            Confirmar ({internalSelected.length})
          </Button>
        </div>
      </div>
    </Modal>
  )
}
